import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { fetchDataSources } from '../../store/slices/dataSourcesSlice';
import FileUploadModal from './FileUploadModal';
import InsertDataFlow from './InsertDataFlow';
import MultiObjectSelector from './MultiObjectSelector';
import * as analyticsApi from '../../api/analytics';

function DataOperationsTab() {
  const dispatch = useDispatch();
  const { selectedDataSource } = useSelector((state) => state.dataSources);
  const { activeTenant } = useSelector((state) => state.auth);

  // Ensure data sources are loaded so InsertDataFlow has a dataSourceId
  useEffect(() => {
    if (activeTenant && !selectedDataSource) {
      dispatch(fetchDataSources());
    }
  }, [dispatch, activeTenant, selectedDataSource]);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showInsertFlow, setShowInsertFlow] = useState(false);
  const [showMultiObjectModal, setShowMultiObjectModal] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [multiObjectConfig, setMultiObjectConfig] = useState([]);

  const handleUploadComplete = (response) => {
    // Add the uploaded file to the list
    setUploadedFiles(prev => [response, ...prev]);
  };

  return (
    <div className="space-y-6">
      {/* Header with action buttons */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Data Operations</h2>
          <p className="text-gray-600 mt-1">
            Upload files to insert, update, upsert, or delete records in your CRM
          </p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => setShowUploadModal(true)}
            className="bg-white text-gray-700 border border-gray-300 px-6 py-2 rounded-lg hover:bg-gray-50 transition font-medium flex items-center gap-2"
            data-testid="dataops-upload-button"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
              />
            </svg>
            Upload File
          </button>
          <button
            onClick={() => setShowInsertFlow(true)}
            className="bg-primary-600 text-white px-6 py-2 rounded-lg hover:bg-primary-700 transition font-medium flex items-center gap-2"
            data-testid="dataops-insert-button"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Insert Records
          </button>
          <button
            onClick={() => setShowMultiObjectModal(true)}
            className="bg-purple-600 text-white px-6 py-2 rounded-lg hover:bg-purple-700 transition font-medium flex items-center gap-2"
            data-testid="dataops-multiobject-button"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h4a1 1 0 011 1v7a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM14 5a1 1 0 011-1h4a1 1 0 011 1v7a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 17a1 1 0 011-1h4a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1v-2zM14 17a1 1 0 011-1h4a1 1 0 011 1v2a1 1 0 01-1 1h-4a1 1 0 01-1-1v-2z" />
            </svg>
            Multi-Object Insert
          </button>
        </div>
      </div>

      {/* Getting Started / Help Section */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <div className="flex items-start gap-4">
          <div className="bg-blue-100 rounded-full p-2 flex-shrink-0">
            <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-blue-900 mb-2">How it works</h3>
            <ol className="space-y-2 text-blue-800 text-sm">
              <li className="flex items-start gap-2">
                <span className="font-bold">1.</span>
                <span>Upload your CSV or Excel file containing the data you want to sync</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="font-bold">2.</span>
                <span>Map your file columns to CRM fields (auto-mapping available)</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="font-bold">3.</span>
                <span>Choose your operation: Insert (create new), Update (modify existing), Upsert (create or update), or Delete</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="font-bold">4.</span>
                <span>Validate your data to catch errors before syncing</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="font-bold">5.</span>
                <span>Execute the operation and download success/failure reports</span>
              </li>
            </ol>
          </div>
        </div>
      </div>

      {/* Operations Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow p-4 border-l-4 border-green-500" data-testid="dataops-stat-insert">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
            </div>
            <div>
              <p className="text-sm text-gray-500">INSERT</p>
              <p className="text-2xl font-bold text-gray-900">0</p>
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-2">Create new records</p>
        </div>

        <div className="bg-white rounded-lg shadow p-4 border-l-4 border-blue-500" data-testid="dataops-stat-update">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </div>
            <div>
              <p className="text-sm text-gray-500">UPDATE</p>
              <p className="text-2xl font-bold text-gray-900">0</p>
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-2">Modify existing records</p>
        </div>

        <div className="bg-white rounded-lg shadow p-4 border-l-4 border-purple-500" data-testid="dataops-stat-upsert">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
              </svg>
            </div>
            <div>
              <p className="text-sm text-gray-500">UPSERT</p>
              <p className="text-2xl font-bold text-gray-900">0</p>
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-2">Create or update by ID</p>
        </div>

        <div className="bg-white rounded-lg shadow p-4 border-l-4 border-red-500" data-testid="dataops-stat-delete">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
              <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </div>
            <div>
              <p className="text-sm text-gray-500">DELETE</p>
              <p className="text-2xl font-bold text-gray-900">0</p>
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-2">Remove records</p>
        </div>
      </div>

      {/* Recent Files */}
      {uploadedFiles.length > 0 && (
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="font-bold text-gray-900">Recent Uploads</h3>
          </div>
          <div className="divide-y divide-gray-200">
            {uploadedFiles.map((file, idx) => (
              <div key={idx} className="px-6 py-4 flex items-center justify-between hover:bg-gray-50">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                    <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{file.filename}</p>
                    <p className="text-sm text-gray-500">
                      {new Date(file.created_at).toLocaleString()}
                    </p>
                  </div>
                </div>
                <button className="text-primary-600 hover:text-primary-700 text-sm font-medium">
                  Map Fields →
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Empty State */}
      {uploadedFiles.length === 0 && (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
              />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No files uploaded yet</h3>
          <p className="text-gray-500 mb-6">
            Get started by uploading a CSV or Excel file with the data you want to sync
          </p>
          <button
            onClick={() => setShowUploadModal(true)}
            className="bg-primary-600 text-white px-6 py-2 rounded-lg hover:bg-primary-700 transition font-medium"
          >
            Upload Your First File
          </button>
        </div>
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <FileUploadModal
          onClose={() => setShowUploadModal(false)}
          onUploadComplete={handleUploadComplete}
        />
      )}

      {/* Insert Data Flow Modal */}
      {showInsertFlow && (
        <InsertDataFlow
          onClose={() => setShowInsertFlow(false)}
        />
      )}

      {/* Multi-Object Insert Modal */}
      {showMultiObjectModal && (
        <MultiObjectModal
          objects={multiObjectConfig}
          onObjectsChange={setMultiObjectConfig}
          onClose={() => setShowMultiObjectModal(false)}
          dataSourceId={selectedDataSource?.data_source_id}
        />
      )}
    </div>
  );
}

// Multi-Object Insert Modal Component
function MultiObjectModal({ objects, onObjectsChange, onClose, dataSourceId }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  // Mock available objects - in production, fetch from API
  const availableObjects = [
    { name: 'Account', label: 'Account' },
    { name: 'Contact', label: 'Contact' },
    { name: 'Opportunity', label: 'Opportunity' },
    { name: 'Lead', label: 'Lead' },
    { name: 'Case', label: 'Case' },
  ];

  const handleSubmit = async () => {
    // Validate all objects have file IDs
    const missingFiles = objects.filter(obj => !obj.fileId);
    if (missingFiles.length > 0) {
      setError(`${missingFiles.length} object(s) missing file ID. Please assign files to all objects.`);
      return;
    }

    if (objects.length === 0) {
      setError('Please add at least one object to process.');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      // Transform objects to API format
      const apiObjects = objects.map(obj => ({
        object_name: obj.objectName,
        file_id: obj.fileId,
        depends_on: obj.dependsOn || []
      }));

      // Call multi-object insert API
      const response = await analyticsApi.insertMultiObjectRecords(apiObjects, dataSourceId);

      setResult(response);
      setError(null);

      // Show success message
      alert(`Multi-object insert started successfully!\nWorkflow ID: ${response.workflow_id}\nProcessing ${response.object_count} objects.`);

      // Close modal after short delay
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (err) {
      console.error('Multi-object insert failed:', err);
      setError(err.message || 'Failed to start multi-object insert operation');
    } finally {
      setSubmitting(false);
    }
  };

  const canSubmit = objects.length > 0 && objects.every(obj => obj.fileId);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col" role="dialog" aria-modal="true" aria-labelledby="multi-object-insert-modal-title">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between bg-purple-50">
          <div>
            <h2 id="multi-object-insert-modal-title" className="text-2xl font-bold text-gray-900">Multi-Object Insert</h2>
            <p className="text-sm text-gray-600 mt-1">
              Process multiple objects in a single operation with automatic dependency handling
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 p-2 rounded-lg hover:bg-white"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-6">
          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
              <svg className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <div className="flex-1">
                <p className="font-medium text-red-900">Error</p>
                <p className="text-sm text-red-800 mt-1">{error}</p>
              </div>
            </div>
          )}

          {result && (
            <div className="mb-4 bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3">
              <svg className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <div className="flex-1">
                <p className="font-medium text-green-900">Success!</p>
                <p className="text-sm text-green-800 mt-1">{result.message}</p>
                <p className="text-xs text-green-700 mt-2">Workflow ID: {result.workflow_id}</p>
              </div>
            </div>
          )}

          <MultiObjectSelector
            availableObjects={availableObjects}
            onObjectsChange={onObjectsChange}
            initialObjects={objects}
          />
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-between items-center">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-white text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition font-medium"
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!canSubmit || submitting}
            className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition font-medium disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {submitting ? (
              <>
                <svg className="animate-spin h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                <span>Processing...</span>
              </>
            ) : (
              <>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span>Submit Multi-Object Insert</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default DataOperationsTab;
